import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import PageMeta from "../components/PageMeta";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

function formatBytes(n) {
  const num = Number(n) || 0;
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
  return `${(num / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function kindLabel(kind) {
  switch (kind) {
    case "pdf":
      return "PDF";
    case "image":
      return "Image";
    case "video":
      return "Video";
    case "link":
      return "Link";
    default:
      return "File";
  }
}

/**
 * Detect a YouTube video ID from any common URL shape:
 *   https://www.youtube.com/watch?v=<id>
 *   https://youtu.be/<id>
 *   https://www.youtube.com/shorts/<id>
 *   https://www.youtube.com/embed/<id>
 *   https://m.youtube.com/watch?v=<id>
 * Returns the 11-char id or "" if none found.
 */
function extractYouTubeId(url) {
  if (!url) return "";
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    if (host === "youtu.be") {
      const id = u.pathname.replace(/^\//, "").split("/")[0] || "";
      return /^[a-zA-Z0-9_-]{11}$/.test(id) ? id : "";
    }
    if (host === "youtube.com" || host === "m.youtube.com" || host === "music.youtube.com") {
      const v = u.searchParams.get("v");
      if (v && /^[a-zA-Z0-9_-]{11}$/.test(v)) return v;
      // /shorts/<id> and /embed/<id>
      const m = u.pathname.match(/^\/(?:shorts|embed)\/([a-zA-Z0-9_-]{11})/);
      if (m) return m[1];
    }
  } catch {
    return "";
  }
  return "";
}

/** Detect a Vimeo video ID from common URL shapes. */
function extractVimeoId(url) {
  if (!url) return "";
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    if (host === "vimeo.com" || host === "player.vimeo.com") {
      const m = u.pathname.match(/\/(\d{6,12})\b/);
      if (m) return m[1];
    }
  } catch {
    return "";
  }
  return "";
}

function LibraryCard({ item, isHighlighted, cardRef }) {
  const fileUrl = `${API}/library/${item.id}/file`;
  const downloadUrl = `${API}/library/${item.id}/download`;

  // For link-kind items, sniff the URL for a YouTube or Vimeo video and
  // embed the player inline so users can watch without leaving the site.
  const ytId = item.kind === "link" ? extractYouTubeId(item.url) : "";
  const vimeoId = item.kind === "link" && !ytId ? extractVimeoId(item.url) : "";

  // Once-per-mount view tracker for inline YT/Vimeo embeds — fires the
  // first time the user mouses over or taps the embed wrapper. Without
  // this, embedded videos would silently rack up plays with no counter
  // since cross-origin iframes don't expose playback events. We
  // deliberately scope to "first engagement" not "every play" because
  // we have no way to detect each play; once per session is the most
  // honest signal available.
  const embedViewTracked = useRef(false);
  const trackEmbedView = () => {
    if (embedViewTracked.current) return;
    embedViewTracked.current = true;
    const url = `${API}/library/${item.id}/track-view`;
    try {
      if (navigator?.sendBeacon) {
        navigator.sendBeacon(url);
      } else {
        axios.post(url).catch(() => {});
      }
    } catch {
      /* analytics ping must never break the UI */
    }
  };

  // ── Shareable link per item ────────────────────────────────────
  // Each item has its own deep-link route at /library/<id>. When that
  // URL is hit, the page scrolls to and highlights this specific
  // card so the recipient lands directly on the resource. The
  // "Copy share link" button copies the FULL absolute URL so it can
  // be pasted into texts, email, social, etc.
  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}/library/${item.id}`
    : `/library/${item.id}`;
  const [copied, setCopied] = useState(false);
  const [shareError, setShareError] = useState("");

  async function copyShareLink() {
    setShareError("");
    setCopied(false);
    try {
      // Prefer the native Web Share API on mobile when available — it
      // pops the OS share sheet (Messages, Mail, WhatsApp, etc.). On
      // desktop fall back to clipboard.
      if (navigator.share && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent || "")) {
        try {
          await navigator.share({ title: item.title, url: shareUrl });
          return;
        } catch (e) {
          // User cancelled the share sheet — fall through to clipboard.
          if (e && e.name === "AbortError") return;
        }
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        // Last-resort fallback for old browsers.
        const ta = document.createElement("textarea");
        ta.value = shareUrl;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      setShareError("Could not copy link");
      setTimeout(() => setShareError(""), 2800);
    }
  }

  return (
    <li
      className={`rm-library-card${isHighlighted ? " is-highlighted" : ""}`}
      ref={cardRef}
      data-testid={`library-card-${item.id}`}
      id={`library-card-${item.id}`}
    >
      <div className="rm-library-card-meta">
        <span className="rm-library-card-size">
          {kindLabel(item.kind)}
          {item.kind !== "link" && item.size_bytes ? ` · ${formatBytes(item.size_bytes)}` : ""}
        </span>
        <span className="rm-library-card-date">{formatDate(item.uploaded_at)}</span>
      </div>

      <h3 className="rm-library-card-title" data-testid={`library-card-title-${item.id}`}>
        {item.title}
      </h3>
      {item.description && <p className="rm-library-card-desc">{item.description}</p>}

      {/* Type-specific media block */}
      {item.kind === "image" && (
        <a
          href={fileUrl}
          target="_blank"
          rel="noreferrer"
          className="rm-library-media-wrap"
          data-testid={`library-card-image-link-${item.id}`}
        >
          <img
            src={fileUrl}
            alt={item.title}
            className="rm-library-media-img"
            loading="lazy"
          />
        </a>
      )}
      {item.kind === "video" && (
        <video
          controls
          preload="metadata"
          className="rm-library-media-video"
          data-testid={`library-card-video-${item.id}`}
        >
          <source src={fileUrl} type={item.mime || "video/mp4"} />
          Your browser does not support inline video.
        </video>
      )}
      {item.kind === "video" && item.paired_with && (
        <div
          className="rm-library-paired-study"
          data-testid={`library-card-paired-study-${item.id}`}
        >
          <div className="rm-library-paired-study-label">
            <span aria-hidden="true">📖</span> Study
          </div>
          <div className="rm-library-paired-study-body">
            <div
              className="rm-library-paired-study-title"
              data-testid={`library-card-paired-study-title-${item.id}`}
            >
              {item.paired_with.title}
            </div>
            {item.paired_with.description && (
              <p className="rm-library-paired-study-desc">
                {item.paired_with.description}
              </p>
            )}
            <a
              className="rm-btn-solid rm-library-paired-study-btn"
              href={`${API}/library/${item.paired_with.id}/download`}
              target="_blank"
              rel="noreferrer"
              data-testid={`library-card-paired-study-download-${item.id}`}
            >
              Download Study (PDF
              {item.paired_with.size_bytes
                ? ` · ${formatBytes(item.paired_with.size_bytes)}`
                : ""}
              )
            </a>
          </div>
        </div>
      )}
      {item.kind === "link" && ytId && (
        <div
          className="rm-library-embed-wrap"
          data-testid={`library-card-yt-${item.id}`}
          onMouseEnter={trackEmbedView}
          onTouchStart={trackEmbedView}
        >
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${ytId}?rel=0`}
            title={item.title}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      )}
      {item.kind === "link" && vimeoId && (
        <div
          className="rm-library-embed-wrap"
          data-testid={`library-card-vimeo-${item.id}`}
          onMouseEnter={trackEmbedView}
          onTouchStart={trackEmbedView}
        >
          <iframe
            src={`https://player.vimeo.com/video/${vimeoId}`}
            title={item.title}
            loading="lazy"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          />
        </div>
      )}

      <div className="rm-library-card-actions">
        {item.kind === "pdf" && (
          <a
            className="rm-btn-solid rm-library-card-btn"
            href={downloadUrl}
            target="_blank"
            rel="noreferrer"
            data-testid={`library-card-download-${item.id}`}
          >
            Download
          </a>
        )}
        {item.kind === "image" && (
          <>
            <a
              className="rm-btn-solid rm-library-card-btn"
              href={fileUrl}
              target="_blank"
              rel="noreferrer"
              data-testid={`library-card-view-${item.id}`}
            >
              View
            </a>
            <a
              className="rm-cm-link rm-library-card-secondary"
              href={downloadUrl}
              target="_blank"
              rel="noreferrer"
              data-testid={`library-card-download-${item.id}`}
            >
              Download
            </a>
          </>
        )}
        {item.kind === "video" && (
          <a
            className="rm-cm-link rm-library-card-secondary"
            href={downloadUrl}
            target="_blank"
            rel="noreferrer"
            data-testid={`library-card-download-${item.id}`}
          >
            Download
          </a>
        )}
        {item.kind === "link" && item.url && (
          <a
            className="rm-btn-solid rm-library-card-btn"
            href={item.url}
            target="_blank"
            rel="noreferrer"
            data-testid={`library-card-open-${item.id}`}
            onClick={() => {
              // Fire-and-forget view-count ping. We use sendBeacon when
              // available so the request survives the navigation that
              // follows when the browser opens the external URL.
              const url = `${API}/library/${item.id}/track-view`;
              try {
                if (navigator?.sendBeacon) {
                  navigator.sendBeacon(url);
                } else {
                  axios.post(url).catch(() => {});
                }
              } catch {
                /* swallow — analytics must never block navigation */
              }
            }}
          >
            {ytId ? "Open on YouTube" : vimeoId ? "Open on Vimeo" : "Open Link"}
          </a>
        )}
        <button
          type="button"
          onClick={copyShareLink}
          className="rm-cm-link rm-library-card-share"
          data-testid={`library-card-share-${item.id}`}
          aria-label={`Copy shareable link for ${item.title}`}
        >
          {copied ? "✓ Link copied" : shareError ? shareError : "Share link"}
        </button>
      </div>
    </li>
  );
}

export default function Library() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Deep-link support — when the route is `/library/:id`, scroll to
  // and highlight that card after the list loads. Each card also has
  // an absolute share URL pointing back here.
  const { id: deepLinkId } = useParams();
  const cardRefs = useRef({});

  useEffect(() => {
    let cancelled = false;
    async function fetchList() {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(`${API}/library`);
        if (!cancelled) setItems(res.data?.items || []);
      } catch (err) {
        if (!cancelled) {
          const detail = err?.response?.data?.detail;
          setError(typeof detail === "string" ? detail : "Could not load the library.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchList();
    return () => {
      cancelled = true;
    };
  }, []);

  // After items load, if we have a deep-link id, scroll the card into
  // view and trigger the highlight pulse. Falls back gracefully when
  // the id is unknown (e.g. item was deleted after the link was shared).
  useEffect(() => {
    if (!deepLinkId || loading || !items.length) return;
    const ref = cardRefs.current[deepLinkId];
    if (ref && ref.scrollIntoView) {
      // Defer one frame so layout is settled.
      requestAnimationFrame(() => {
        ref.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
  }, [deepLinkId, loading, items]);

  // Look up the deep-link item's title so we can show a small "Direct
  // link to ..." banner above the grid when the user arrived via a
  // shared URL. Reassuring confirmation that the link worked.
  const deepLinkItem = useMemo(() => {
    if (!deepLinkId) return null;
    return items.find((it) => it.id === deepLinkId) || null;
  }, [deepLinkId, items]);

  return (
    <main className="relative min-h-screen" data-testid="library-page">
      <PageMeta
        title="Downloadable Content Library"
        description="Free PDFs, teachings, prayers, and Bible-study resources from the Restoration Matrix. Open to all — no sign-up, no account, no fee."
        path="/library"
      />
      <div className="rm-cm-bg" aria-hidden="true" />

      <header className="relative z-10 px-6 sm:px-12 md:px-20 py-10 flex items-center justify-between">
        <Link
          to="/"
          className="font-cinzel text-xs sm:text-sm tracking-[0.28em] uppercase text-[#FFE7A0]/85 hover:text-[#FFE7A0]"
          data-testid="library-home-link"
        >
          ← The Restoration Matrix
        </Link>
      </header>

      <section className="relative z-10 max-w-3xl mx-auto px-6 sm:px-10 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.25, 0.1, 0.25, 1] }}
          className="text-center"
        >
          <div
            className="font-cinzel text-[10px] sm:text-xs tracking-[0.32em] uppercase text-[#FFE7A0]/80 mb-6"
            data-testid="library-overline"
          >
            Downloadable Content
          </div>
          <h1
            className="font-serif-display italic text-4xl sm:text-5xl text-[#FFE7A0]"
            data-testid="library-title"
          >
            The Library.
          </h1>
          <p
            className="mt-6 font-cinzel text-sm sm:text-base uppercase text-white font-semibold tracking-[0.18em] max-w-xl mx-auto leading-[1.85]"
            data-testid="library-mini"
            style={{ textShadow: "0 0 14px rgba(5,8,24,1), 0 0 28px rgba(5,8,24,0.95), 0 2px 4px rgba(5,8,24,1)" }}
          >
            Free PDFs, images, videos, and links &middot; teachings, prayers, and resources from the Matrix
          </p>
        </motion.div>

        {error && (
          <div className="rm-cm-alert rm-cm-alert-error mt-10" data-testid="library-error">
            {error}
          </div>
        )}

        {deepLinkItem && !error && (
          <div
            className="rm-library-deeplink-banner"
            data-testid="library-deeplink-banner"
          >
            <span className="rm-library-deeplink-icon">🔗</span>
            Direct link to <em>{deepLinkItem.title}</em> — opening it for you below.
          </div>
        )}

        {deepLinkId && !deepLinkItem && !loading && !error && (
          <div className="rm-cm-alert rm-cm-alert-info mt-10" data-testid="library-deeplink-missing">
            The item you're looking for is no longer available, but you're welcome to browse the rest of the library below.
          </div>
        )}

        <div className="mt-12" data-testid="library-list">
          {loading && (
            <div className="rm-cm-empty" data-testid="library-loading">
              Loading…
            </div>
          )}
          {!loading && items.length === 0 && !error && (
            <div className="rm-cm-empty" data-testid="library-empty">
              No items have been added yet. Check back soon.
            </div>
          )}
          {!loading && items.length > 0 && (
            <ul className="rm-library-grid">
              {items.map((it) => (
                <LibraryCard
                  key={it.id}
                  item={it}
                  isHighlighted={deepLinkId === it.id}
                  cardRef={(el) => {
                    if (el) cardRefs.current[it.id] = el;
                    else delete cardRefs.current[it.id];
                  }}
                />
              ))}
            </ul>
          )}
        </div>
      </section>
    </main>
  );
}
